import React, { useState, useEffect, useRef } from "react";
import { styled } from "@mui/material/styles";
import MuiAppBar from "@mui/material/AppBar";
import title from "../../../components/title";
import Box from "@mui/material/Box";
import "./OwnerRegistration.css";
import Toolbar from "@mui/material/Toolbar";
import {
  Button,
  Container,
  MenuItem,
  Select,
  TextField,
  Typography,
  InputAdornment,
  CircularProgress,
  IconButton,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Paper,
  Grid,
  Avatar,
  FormHelperText,
  Card,
  CardContent,
  Backdrop,
} from "@mui/material";
import {
  LogoutRounded,
  PhotoCamera,
  Person as PersonIcon,
  CreditCard as CnicIcon,
  Work as WorkIcon,
} from "@mui/icons-material";
import OtsLogo from "../../../components/OtsLogo";
import { useTheme as useAppTheme } from "../../../context/ThemeContext";
import { OwnerRegister } from "../../../redux/reducer/OwnerRegistration";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import { ValidateUser } from "../../../redux/reducer/ValidateUser";
import {
  ownerRegistrationSchema,
  occupationOptions,
  monthlyIncomeOptions,
  getSourceOfIncomeOptionsFor,
} from "./validationSchema";
import { useFormik } from "formik";
import LogoutHelper from "../../../Helper/LogoutHelper";
import { useNavigate } from "react-router-dom";
import SignatureCapture from "../../../components/SignatureCapture";
import DrawIcon from "@mui/icons-material/Draw";

const drawerWidth = 240;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

export default function OwnerRegistration() {
  title("Owner Registration");
  const { theme: appTheme } = useAppTheme();
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const formRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sameAsCurrentAddress, setSameAsCurrentAddress] = useState(false);
  const sameAsCurrentAddressRef = useRef(false);

  // Image previews
  const [profilePreview, setProfilePreview] = useState(null);
  const [cnicFrontPreview, setCnicFrontPreview] = useState(null);
  const [cnicBackPreview, setCnicBackPreview] = useState(null);

  // File input refs
  const profileInputRef = useRef(null);
  const cnicFrontInputRef = useRef(null);
  const cnicBackInputRef = useRef(null);

  const { loginUser } = useSelector((state) => state);
  const ownerRegistrationLoading = useSelector(
    (state) => state.ownerRegistration?.loading
  );

  const initialValues = {
    ownerName: "",
    fatherName: "",
    cnic: "",
    contactNumber: "",
    alternateNumber: "",
    currentAddress: "",
    permanentAddress: "",
    occupation: "",
    sourceOfIncome: "",
    monthlyIncome: "",
    profilePhoto: null,
    cnicFront: null,
    cnicBack: null,
    signature: "",
  };

  const formik = useFormik({
    initialValues,
    validationSchema: ownerRegistrationSchema,
    onSubmit: async (values) => {
      handleFinalSubmit(values);
    },
  });

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
    submitCount,
  } = formik;

  // CNIC formatting
  const handleCnicChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 13) value = value.slice(0, 13);

    let formatted = value;
    if (value.length > 5) {
      formatted = value.slice(0, 5) + "-" + value.slice(5);
    }
    if (value.length > 12) {
      formatted = formatted.slice(0, 13) + "-" + formatted.slice(13);
    }

    setFieldValue("cnic", formatted);
  };

  // Keep Source of Income limited to options relevant to the chosen Occupation
  const handleOccupationChange = (e) => {
    handleChange(e);
    const allowedValues = getSourceOfIncomeOptionsFor(e.target.value).map(
      (opt) => opt.value
    );
    if (values.sourceOfIncome && !allowedValues.includes(values.sourceOfIncome)) {
      setFieldValue("sourceOfIncome", "");
    }
  };

  // Contact number formatting
  const handleContactChange = (e, fieldName) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 9) value = value.slice(0, 9);
    setFieldValue(fieldName, value);
  };

  // Handle image upload
  const handleImageUpload = (e, fieldName, setPreview) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Image size must be less than 2MB", {
          position: "top-center",
        });
        return;
      }
      if (!["image/jpeg", "image/png", "image/jpg"].includes(file.type)) {
        toast.error("Only jpg or jpeg or png files are allowed", {
          position: "top-center",
        });
        return;
      }
      setFieldValue(fieldName, file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Sync permanent address with current address
  useEffect(() => {
    sameAsCurrentAddressRef.current = sameAsCurrentAddress;
    if (sameAsCurrentAddress && values.currentAddress) {
      setFieldValue("permanentAddress", values.currentAddress);
    }
  }, [sameAsCurrentAddress, values.currentAddress, setFieldValue]);

  const handleFinalSubmit = async (values) => {
    setIsSubmitting(true);

    try {
      const formData = new FormData();

      formData.append("ownerName", values.ownerName);
      formData.append("fatherName", values.fatherName);
      formData.append("cnic", values.cnic);
      formData.append("contactNumber", `03${values.contactNumber}`);
      formData.append(
        "alternateNumber",
        values.alternateNumber ? `03${values.alternateNumber}` : ""
      );
      formData.append("currentAddress", values.currentAddress);
      formData.append(
        "permanentAddress",
        sameAsCurrentAddressRef.current
          ? values.currentAddress
          : values.permanentAddress
      );
      formData.append("occupation", values.occupation);
      formData.append("sourceOfIncome", values.sourceOfIncome);
      formData.append("monthlyIncome", values.monthlyIncome || "");
      formData.append("userId", sessionStorage.getItem("user_id") || "");

      // ✅ IMAGES AS BINARY
      if (values.profilePhoto) {
        formData.append("profilePhoto", values.profilePhoto);
      }
      if (values.cnicFront) {
        formData.append("cnicFront", values.cnicFront);
      }
      if (values.cnicBack) {
        formData.append("cnicBack", values.cnicBack);
      }

      // ✅ SIGNATURE AS BASE64 STRING
      if (values.signature) {
        formData.append("signature", values.signature);
      }

      const res = await dispatch(
        OwnerRegister(formData) // 👈 direct FormData
      );

      if (res?.payload?.data?.message === "Owner Registered Successfully") {
        toast.success("Owner Registered Successfully!", {
          position: "top-center",
        });

        sessionStorage.setItem("is_register", true);
        sessionStorage.setItem("is_active", true);

        const token = loginUser?.login?.data?.data?.password;
        await dispatch(
          ValidateUser({
            email: loginUser?.login?.data?.data?.email,
            token,
          })
        );
        setTimeout(() => {
          navigate("/landlord-dashboard");
        }, 3200);
      } else {
        console.log(res);

        toast.error(
          res?.payload?.data?.message ||
            res?.payload?.message ||
            "Registration failed",
          {
            position: "top-center",
          }
        );
      }
    } catch (error) {
      toast.error("Something went wrong", { position: "top-center" });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (submitCount > 0 && Object.keys(errors).length > 0) {
      const form = formRef.current;

      if (!form) return;

      // first element having MUI error
      const errorElement =
        form.querySelector(".Mui-error") ||
        form.querySelector('[aria-invalid="true"]');

      if (errorElement) {
        errorElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        // focus if possible
        if (typeof errorElement.focus === "function") {
          errorElement.focus();
        }
      }
    }
  }, [errors, submitCount]);

  return (
    <>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(255, 255, 255, 0.8)",
        }}
        open={ownerRegistrationLoading || isSubmitting}
      >
        <CircularProgress color="primary" />
      </Backdrop>

      {/* Brand header — the logo mark is drawn from the theme instead of the
          flat artwork, so it stays crisp at toolbar height and the wordmark
          reads as text rather than a shrunken image. */}
      <AppBar position="fixed" elevation={0}>
        <Toolbar
          sx={{
            minHeight: { xs: 64, md: 74 },
            px: { xs: 1.5, md: 3 },
            gap: 1.5,
            justifyContent: "space-between",
            background: `linear-gradient(115deg, ${appTheme.navBg} 0%, ${appTheme.navBg} 45%, ${appTheme.primary}40 100%)`,
            borderBottom: `1px solid ${appTheme.secondary}33`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.4, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 44,
                width: 44,
                flexShrink: 0,
                borderRadius: "14px",
                bgcolor: "rgba(255,255,255,0.1)",
                border: `1px solid ${appTheme.secondary}40`,
              }}
            >
              <OtsLogo height={30} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                sx={{
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: { xs: 15, md: 17 },
                  lineHeight: 1.2,
                  letterSpacing: 0.3,
                }}
              >
                Owner Tenant Solution
              </Typography>
              <Typography
                noWrap
                sx={{
                  color: appTheme.secondary,
                  fontSize: { xs: 9.5, md: 10.5 },
                  letterSpacing: 1.6,
                  textTransform: "uppercase",
                }}
              >
                Owner Registration
              </Typography>
            </Box>
          </Box>

          <Button
            onClick={() => LogoutHelper.logout()}
            startIcon={<LogoutRounded />}
            sx={{
              flexShrink: 0,
              color: "rgba(255,255,255,0.85)",
              borderRadius: "999px",
              px: { xs: 1.4, md: 2 },
              border: "1px solid rgba(255,255,255,0.18)",
              "& .MuiButton-startIcon": { mr: { xs: 0, sm: 1 } },
              "&:hover": {
                color: "#fff",
                bgcolor: "rgba(244,67,54,0.22)",
                borderColor: "rgba(244,67,54,0.45)",
              },
            }}
          >
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
              Logout
            </Box>
          </Button>
        </Toolbar>
      </AppBar>

      {/* `--ots-primary` feeds the plain-CSS rules in OwnerRegistration.css */}
      <Container maxWidth="md" sx={{ "--ots-primary": appTheme.primary }}>
        <Box mt={{ xs: 11, md: 13 }} mb={4}>
          <Typography
            variant="h4"
            align="center"
            fontWeight="bold"
            gutterBottom
            sx={{
              background: `linear-gradient(120deg, ${appTheme.primary} 0%, ${appTheme.secondary} 100%)`,
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
              letterSpacing: 0.5,
            }}
          >
            OWNER REGISTRATION
          </Typography>
          <Typography
            variant="subtitle1"
            align="center"
            color="textSecondary"
            mb={3}
          >
            Please provide your personal information to register as a property
            owner
          </Typography>

          <form onSubmit={handleSubmit} ref={formRef}>
            <Paper sx={{ p: 3, mb: 3 }} elevation={3}>
              {/* Profile Photo Section */}
              <Box className="section-title" sx={{ mb: 3 }}>
                <PersonIcon color="primary" />
                <Typography variant="h6" sx={{ ml: 1 }}>
                  Profile Photo
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                <Box sx={{ textAlign: "center" }}>
                  <Avatar
                    src={profilePreview}
                    sx={{ width: 120, height: 120, margin: "0 auto", mb: 2 }}
                  />
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    ref={profileInputRef}
                    onChange={(e) =>
                      handleImageUpload(e, "profilePhoto", setProfilePreview)
                    }
                  />
                  <Button
                    variant="outlined"
                    startIcon={<PhotoCamera />}
                    onClick={() => profileInputRef.current.click()}
                  >
                    Upload Photo
                  </Button>
                  {touched.profilePhoto && errors.profilePhoto && (
                    <FormHelperText error sx={{ textAlign: "center" }}>
                      {errors.profilePhoto}
                    </FormHelperText>
                  )}
                </Box>
              </Box>

              {/* Personal Information */}
              <Box className="section-title" sx={{ mb: 2, mt: 4 }}>
                <PersonIcon color="primary" />
                <Typography variant="h6" sx={{ ml: 1 }}>
                  Personal Information
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    id="ownerName"
                    name="ownerName"
                    label="Owner Name *"
                    value={values.ownerName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.ownerName && Boolean(errors.ownerName)}
                    helperText={touched.ownerName && errors.ownerName}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    id="fatherName"
                    name="fatherName"
                    label="Father Name *"
                    value={values.fatherName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.fatherName && Boolean(errors.fatherName)}
                    helperText={touched.fatherName && errors.fatherName}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    id="cnic"
                    name="cnic"
                    label="CNIC Number *"
                    placeholder="XXXXX-XXXXXXX-X"
                    value={values.cnic}
                    onChange={handleCnicChange}
                    onBlur={handleBlur}
                    error={touched.cnic && Boolean(errors.cnic)}
                    helperText={touched.cnic && errors.cnic}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    id="contactNumber"
                    name="contactNumber"
                    label="Contact Number *"
                    value={values.contactNumber}
                    onChange={(e) => handleContactChange(e, "contactNumber")}
                    onBlur={handleBlur}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">03</InputAdornment>
                      ),
                    }}
                    error={
                      touched.contactNumber && Boolean(errors.contactNumber)
                    }
                    helperText={touched.contactNumber && errors.contactNumber}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    id="alternateNumber"
                    name="alternateNumber"
                    label="Alternate Number (Optional)"
                    value={values.alternateNumber}
                    onChange={(e) => handleContactChange(e, "alternateNumber")}
                    onBlur={handleBlur}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">03</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              {/* CNIC Images */}
              <Box className="section-title" sx={{ mb: 2, mt: 4 }}>
                <CnicIcon color="primary" />
                <Typography variant="h6" sx={{ ml: 1 }}>
                  CNIC Images
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="subtitle2" gutterBottom>
                      CNIC Front Side *
                    </Typography>
                    {cnicFrontPreview ? (
                      <Box
                        component="img"
                        src={cnicFrontPreview}
                        sx={{
                          width: "100%",
                          maxHeight: 150,
                          objectFit: "contain",
                          mb: 2,
                          borderRadius: 1,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 150,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "#f5f5f5",
                          borderRadius: 1,
                          mb: 2,
                        }}
                      >
                        <CnicIcon sx={{ fontSize: 60, color: "#ccc" }} />
                      </Box>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      ref={cnicFrontInputRef}
                      onChange={(e) =>
                        handleImageUpload(e, "cnicFront", setCnicFrontPreview)
                      }
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PhotoCamera />}
                      onClick={() => cnicFrontInputRef.current.click()}
                    >
                      Upload Front
                    </Button>
                    {touched.cnicFront && errors.cnicFront && (
                      <FormHelperText error>{errors.cnicFront}</FormHelperText>
                    )}
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="subtitle2" gutterBottom>
                      CNIC Back Side *
                    </Typography>
                    {cnicBackPreview ? (
                      <Box
                        component="img"
                        src={cnicBackPreview}
                        sx={{
                          width: "100%",
                          maxHeight: 150,
                          objectFit: "contain",
                          mb: 2,
                          borderRadius: 1,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 150,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "#f5f5f5",
                          borderRadius: 1,
                          mb: 2,
                        }}
                      >
                        <CnicIcon sx={{ fontSize: 60, color: "#ccc" }} />
                      </Box>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      ref={cnicBackInputRef}
                      onChange={(e) =>
                        handleImageUpload(e, "cnicBack", setCnicBackPreview)
                      }
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PhotoCamera />}
                      onClick={() => cnicBackInputRef.current.click()}
                    >
                      Upload Back
                    </Button>
                    {touched.cnicBack && errors.cnicBack && (
                      <FormHelperText error>{errors.cnicBack}</FormHelperText>
                    )}
                  </Card>
                </Grid>
              </Grid>

              {/* Address Information */}
              <Box className="section-title" sx={{ mb: 2, mt: 4 }}>
                <PersonIcon color="primary" />
                <Typography variant="h6" sx={{ ml: 1 }}>
                  Address Information
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="currentAddress"
                    name="currentAddress"
                    label="Current Address *"
                    multiline
                    rows={2}
                    value={values.currentAddress}
                    onChange={(e) => {
                      handleChange(e);
                      if (sameAsCurrentAddress) {
                        setFieldValue("permanentAddress", e.target.value);
                      }
                    }}
                    onBlur={handleBlur}
                    error={
                      touched.currentAddress && Boolean(errors.currentAddress)
                    }
                    helperText={touched.currentAddress && errors.currentAddress}
                  />
                </Grid>
                {values.currentAddress && (
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={sameAsCurrentAddress}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setSameAsCurrentAddress(isChecked);
                            if (isChecked) {
                              setFieldValue(
                                "permanentAddress",
                                values.currentAddress
                              );
                            } else {
                              setFieldValue("permanentAddress", "");
                            }
                          }}
                        />
                      }
                      label="Permanent Address is same as Current Address"
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="permanentAddress"
                    name="permanentAddress"
                    label="Permanent Address *"
                    multiline
                    rows={2}
                    value={values.permanentAddress}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={sameAsCurrentAddress}
                    error={
                      touched.permanentAddress &&
                      Boolean(errors.permanentAddress)
                    }
                    helperText={
                      touched.permanentAddress && errors.permanentAddress
                    }
                  />
                </Grid>
              </Grid>

              {/* Occupation & Income Information */}
              <Box className="section-title" sx={{ mb: 2, mt: 4 }}>
                <WorkIcon color="primary" />
                <Typography variant="h6" sx={{ ml: 1 }}>
                  Occupation & Income
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl
                    fullWidth
                    error={touched.occupation && Boolean(errors.occupation)}
                  >
                    <InputLabel>Occupation *</InputLabel>
                    <Select
                      id="occupation"
                      name="occupation"
                      value={values.occupation}
                      label="Occupation *"
                      onChange={handleOccupationChange}
                      onBlur={handleBlur}
                    >
                      {occupationOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.occupation && errors.occupation && (
                      <FormHelperText>{errors.occupation}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl
                    fullWidth
                    error={
                      touched.sourceOfIncome && Boolean(errors.sourceOfIncome)
                    }
                  >
                    <InputLabel>Source of Income *</InputLabel>
                    <Select
                      id="sourceOfIncome"
                      name="sourceOfIncome"
                      value={values.sourceOfIncome}
                      label="Source of Income *"
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      {getSourceOfIncomeOptionsFor(values.occupation).map(
                        (option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        )
                      )}
                    </Select>
                    {touched.sourceOfIncome && errors.sourceOfIncome && (
                      <FormHelperText>{errors.sourceOfIncome}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl
                    fullWidth
                    error={
                      touched.monthlyIncome && Boolean(errors.monthlyIncome)
                    }
                  >
                    <InputLabel>Monthly Income *</InputLabel>
                    <Select
                      id="monthlyIncome"
                      name="monthlyIncome"
                      value={values.monthlyIncome}
                      label="Monthly Income"
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      {monthlyIncomeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.monthlyIncome && errors.monthlyIncome && (
                      <FormHelperText>{errors.monthlyIncome}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>

              {/* Digital Signature Section */}
              <Box className="section-title" sx={{ mb: 2, mt: 4 }}>
                <DrawIcon color="primary" />
                <Typography variant="h6" sx={{ ml: 1 }}>
                  Digital Signature
                </Typography>
              </Box>

              <Card variant="outlined" sx={{ p: 2 }}>
                <CardContent>
                  <SignatureCapture
                    label="Your Digital Signature *"
                    existingSignature={values.signature}
                    onSignatureChange={(sig) => setFieldValue("signature", sig)}
                    error={touched.signature && !values.signature}
                    helperText={
                      touched.signature && !values.signature
                        ? "Digital signature is required"
                        : "Please sign in the box above. This signature will be used in agreements."
                    }
                    primaryColor={appTheme.primary}
                  />
                </CardContent>
              </Card>
            </Paper>

            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                type="submit"
                size="large"
                disabled={isSubmitting}
                sx={{ minWidth: 200 }}
              >
                {isSubmitting ? "Registering..." : "Register"}
              </Button>
            </Box>
          </form>
        </Box>
      </Container>

      <ToastContainer />
    </>
  );
}
